import { File, ImageFormat, VideoFormat } from '../enums';
import { FileFragment } from '../graphql/generated/graphql';

export const getFileType = (file: FileFragment): File =>
  file.mime.split('/')[0] as File;

export const isVideoFile = (file: FileFragment): boolean =>
  getFileType(file) === File.Video;

const _buildStrapiFileUrl = (hash: string, ext: string): string => {
  const baseURL = import.meta.env.VITE_AWS_CF_URL;
  const imageFilePath = `/${hash}${ext}`;
  if (!baseURL) return imageFilePath;
  return `${baseURL}${imageFilePath}`;
};

const getStrapiFileUrl = <T extends ImageFormat | VideoFormat>(
  file: FileFragment,
  format: T,
  fallbackOrder: T[]
): string => {
  const formatIndex = fallbackOrder.indexOf(format);

  if (formatIndex === -1) {
    throw new Error(`Invalid format: ${format}`);
  }

  for (let i = formatIndex; i < fallbackOrder.length; i++) {
    const currentFormat = fallbackOrder[i];
    const formatFile = file.formats?.[currentFormat];

    if (formatFile?.ext) {
      return _buildStrapiFileUrl(formatFile.hash, formatFile.ext);
    }
  }

  if (file.ext) {
    return _buildStrapiFileUrl(file.hash, file.ext);
  }

  return file.url;
};

export const getStrapiImageUrl = (
  file: FileFragment,
  format: ImageFormat = ImageFormat.SmallWebp
): string => {
  return getStrapiFileUrl(file, format, Object.values(ImageFormat));
};

export const getStrapiVideoUrl = (
  file: FileFragment,
  format: VideoFormat
): string => {
  return getStrapiFileUrl(file, format, Object.values(VideoFormat));
};

export const generateImageSrcSet = (file: FileFragment): string => {
  const formats = file.formats || {};

  const formatPriority = ['webp', 'jpg', 'png'];

  const prioritizedFormats = Object.keys(formats).sort((a, b) => {
    const aPriority = formatPriority.findIndex((format) =>
      formats[a].ext.includes(format)
    );
    const bPriority = formatPriority.findIndex((format) =>
      formats[b].ext.includes(format)
    );
    return aPriority - bPriority;
  });

  const srcSetEntries = prioritizedFormats
    .map((format) => {
      const formatInfo = formats[format];

      if (formatInfo?.width) {
        return `${getStrapiImageUrl(file, format as ImageFormat)} ${formatInfo.width}w`;
      }

      return null;
    })
    .filter(Boolean);

  return srcSetEntries.join(', ');
};

export const getVideoSources = (
  file: FileFragment,
  resolutions: ('sd' | 'hd' | 'full_hd')[]
): { src: string; type: string }[] => {
  const formats = file.formats || {};

  const resolutionToFormats = {
    sd: [VideoFormat.SdMp4],
    hd: [VideoFormat.HdWebm, VideoFormat.HdMp4],
    full_hd: [VideoFormat.FullHdWebm, VideoFormat.FullHdMp4],
  };

  const desiredFormats = resolutions.flatMap(
    (resolution) => resolutionToFormats[resolution]
  );

  const formatPriority = ['webm', 'mp4'];

  const prioritizedFormats = Object.keys(formats)
    .filter((format) => desiredFormats.includes(format as VideoFormat))
    .sort((a, b) => {
      const aPriority = formatPriority.findIndex((format) =>
        formats[a].ext.includes(format)
      );
      const bPriority = formatPriority.findIndex((format) =>
        formats[b].ext.includes(format)
      );
      return aPriority - bPriority;
    });

  return prioritizedFormats
    .map((format) => {
      const formatInfo = formats[format];

      if (formatInfo.mime) {
        return {
          src: getStrapiVideoUrl(file, format as VideoFormat),
          type: formatInfo.mime,
        };
      }

      return null;
    })
    .filter(Boolean) as { src: string; type: string }[];
};
